<template>
  <div>
    <div class="d-flex flex-wrap align-center gap-2 mb-4">
      <h6 class="mb-0">Logs de mensagens</h6>
      <v-spacer />
      <v-btn icon :loading="loading" :disabled="loading" @click="loadMessages">
        <v-icon>mdi-refresh</v-icon>
      </v-btn>
    </div>

    <v-row dense class="mb-2">
      <v-col v-if="global" cols="12" sm="6" md="3">
        <v-autocomplete
          v-model="filters.companyId"
          :items="companies"
          item-text="name"
          item-value="id"
          label="Empresa"
          outlined
          dense
          hide-details
          clearable
        />
      </v-col>
      <v-col cols="12" sm="6" md="3">
        <PlatformSelector
          v-model="platform"
          label="Plataforma"
          clearable
          v-bind="global ? { items: filteredCatalogPlatforms } : {}"
        />
      </v-col>
      <v-col cols="12" sm="6" md="3">
        <v-select
          v-model="filters.state"
          :items="MESSAGE_STATE_OPTIONS"
          item-text="text"
          item-value="value"
          label="Status"
          outlined
          dense
          hide-details
          clearable
        />
      </v-col>
      <v-col cols="12" sm="6" md="3">
        <v-select
          v-model="filters.templateType"
          :items="templateTypes"
          item-text="title"
          item-value="key"
          label="Tipo de mensagem"
          outlined
          dense
          hide-details
          clearable
        />
      </v-col>
      <v-col cols="12" sm="6" md="3">
        <phone-input
          :phone="filters.phone"
          :ddi="filters.ddi"
          dense
          hide-details
          optional
          :validate="false"
          label="Buscar por número"
          @update:phone="(e) => (filters.phone = e)"
          @update:ddi="(e) => (filters.ddi = e)"
        />
      </v-col>
    </v-row>

    <v-alert v-if="error" type="error" dense class="mb-2">{{ error }}</v-alert>

    <v-data-table
      :headers="headers"
      :items="messages"
      :options.sync="options"
      :server-items-length="total"
      :loading="loading"
      :footer-props="{ 'items-per-page-options': [10, 25, 50, 100] }"
      show-expand
      single-expand
      dense
      class="elevation-0"
    >
      <template v-slot:item.platformId="{ item }">
        <div class="d-flex align-center gap-1">
          <div>
            <v-img
              v-if="platformMap[item.platformId]?.logos?.favicon"
              :src="platformMap[item.platformId].logos.favicon"
              width="20"
              height="20"
              class="rounded-xl flex-shrink-0"
            />
          </div>
          <div>
            <span>{{ platformMap[item.platformId]?.name || "—" }}</span>
            <div
              v-if="global && platformMap[item.platformId]?.company?.name"
              class="text-caption text--secondary"
            >
              {{ platformMap[item.platformId].company.name }}
            </div>
          </div>
        </div>
      </template>

      <template v-slot:item.recipient="{ item }">
        <span class="text-no-wrap">{{
          formatRecipient(item.recipient, item.channel)
        }}</span>
      </template>

      <template v-slot:item.channel="{ item }">
        <v-chip
          x-small
          label
          :color="MESSAGE_CHANNELS[item.channel]?.color || 'grey'"
          dark
        >
          {{ MESSAGE_CHANNELS[item.channel]?.text || item.channel }}
        </v-chip>
      </template>

      <template v-slot:item.templateType="{ item }">
        <div v-if="item.templateType">
          <span>{{ typeTitle(item.templateType) }}</span>
          <div class="text-caption text--secondary">
            {{ item.templateType }}
          </div>
        </div>
        <span v-else>—</span>
      </template>

      <template v-slot:item.state="{ item }">
        <v-chip
          x-small
          label
          :color="MESSAGE_STATES[item.state]?.color || 'grey'"
          dark
          class="w-full justify-center"
        >
          {{ MESSAGE_STATES[item.state]?.text || item.state }}
        </v-chip>
      </template>

      <template v-slot:item.createdAt="{ item }">
        <date-cell :value="item.createdAt" />
      </template>

      <template v-slot:item.dispatchedAt="{ item }">
        <date-cell :value="item.dispatchedAt" />
      </template>

      <template v-slot:item.deliveredAt="{ item }">
        <date-cell :value="item.deliveredAt" />
      </template>

      <template v-slot:item.completedAt="{ item }">
        <date-cell :value="item.completedAt" />
      </template>

      <template v-slot:expanded-item="{ headers: hdrs, item }">
        <td :colspan="hdrs.length" class="pa-4 grey lighten-5">
          <v-row dense>
            <v-col cols="12" sm="6" md="4">
              <div class="text-caption text--secondary">Canal</div>
              <div>
                {{ MESSAGE_CHANNELS[item.channel]?.text || item.channel }}
              </div>
            </v-col>
            <v-col cols="12" sm="6" md="4">
              <div class="text-caption text--secondary">
                Provider Message ID
              </div>
              <div class="text-break">{{ item.providerMessageId || "—" }}</div>
            </v-col>
            <v-col cols="12" sm="6" md="4">
              <div class="text-caption text--secondary">Tentativas</div>
              <div>{{ item.attempts ?? 0 }}</div>
            </v-col>
            <v-col v-if="item.errorCode || item.errorReason" cols="12">
              <div class="text-caption text--secondary">Erro</div>
              <div class="text-error">
                <span v-if="item.errorCode">[{{ item.errorCode }}]</span>
                {{ item.errorReason || "" }}
              </div>
            </v-col>
            <v-col cols="12">
              <div class="text-caption text--secondary mb-1">Variáveis</div>
              <pre
                class="text-caption pa-2 rounded grey lighten-4 mb-0 overflow-auto"
                >{{ formatJson(item.variables) }}</pre
              >
            </v-col>
          </v-row>
        </td>
      </template>
    </v-data-table>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import ADMIN_MESSAGER from "@/services/admin/messager.js";
import ULTIMATE_MESSAGER from "@/services/ultimate/messager.js";
import PlatformSelector from "@/components/global/PlatformSelector.vue";
import DateCell from "@/components/admin/messager/DateCell.vue";
import PhoneInput from "@/components/global/PhoneInput.vue";
import {
  MESSAGE_STATES,
  MESSAGE_CHANNELS,
  MESSAGE_STATE_OPTIONS,
  formatRecipient,
} from "@/utils/messagerMessages.js";

export default {
  name: "MessageLogsList",
  components: { PlatformSelector, DateCell, PhoneInput },
  props: {
    global: {
      type: Boolean,
      default: false,
    },
  },
  data: () => ({
    loading: false,
    error: null,
    messages: [],
    total: 0,
    platformMap: {},
    templateTypes: [],
    catalogPlatforms: [],
    companies: [],
    platform: null,
    filters: {
      state: null,
      templateType: null,
      phone: "",
      ddi: 55,
      companyId: null,
    },
    phoneDebounce: null,
    options: {
      page: 1,
      itemsPerPage: 25,
      sortBy: ["createdAt"],
      sortDesc: [true],
    },
    MESSAGE_STATES,
    MESSAGE_CHANNELS,
    MESSAGE_STATE_OPTIONS,
    formatRecipient,
    headers: [
      { text: "", value: "data-table-expand", width: 40, sortable: false },
      { text: "Plataforma", value: "platformId", sortable: false },
      { text: "Destinatário", value: "recipient", sortable: false },
      { text: "Canal", value: "channel", sortable: false },
      { text: "Tipo", value: "templateType", sortable: false },
      { text: "Status", value: "state", sortable: false },
      { text: "Criada", value: "createdAt", sortable: false },
      { text: "Despachada", value: "dispatchedAt", sortable: false },
      { text: "Entregue", value: "deliveredAt", sortable: false },
      { text: "Concluída", value: "completedAt", sortable: false },
    ],
  }),
  computed: {
    ...mapGetters("company", ["selectedCompany"]),
    messagerApi() {
      return this.global ? ULTIMATE_MESSAGER : ADMIN_MESSAGER;
    },
    filteredCatalogPlatforms() {
      if (!this.filters.companyId) return this.catalogPlatforms;
      return this.catalogPlatforms.filter(
        (p) => p.companyId === this.filters.companyId
      );
    },
  },
  methods: {
    typeTitle(key) {
      const t = this.templateTypes.find((x) => x.key === key);
      return t?.title || key;
    },
    formatJson(value) {
      if (value == null) return "—";
      try {
        return JSON.stringify(value, null, 2);
      } catch {
        return String(value);
      }
    },
    buildParams() {
      const { page, itemsPerPage } = this.options;
      const params = {
        take: itemsPerPage,
        skip: (page - 1) * itemsPerPage,
      };
      if (this.platform?.id) params.platformId = this.platform.id;
      if (this.filters.companyId) params.companyId = this.filters.companyId;
      if (this.filters.state) params.state = this.filters.state;
      if (this.filters.templateType)
        params.templateType = this.filters.templateType;
      if (this.filters.phone?.trim()) params.phone = this.filters.phone.trim();
      return params;
    },
    async loadCatalog() {
      const data = await this.messagerApi.getCatalog();
      this.templateTypes = data.templateTypes || [];
      if (this.global) {
        this.catalogPlatforms = data.platforms || [];
        this.companies = data.companies || [];
      }
    },
    async loadMessages() {
      try {
        this.loading = true;
        this.error = null;
        const data = await this.messagerApi.messages.getAll(this.buildParams());
        this.messages = data.messages || [];
        if (data.total != null) this.total = data.total;
        const platforms = data.platforms || [];
        this.platformMap = Object.fromEntries(platforms.map((p) => [p.id, p]));
      } catch (err) {
        this.error = err.message || "Erro ao carregar logs";
      } finally {
        this.loading = false;
      }
    },
    schedulePhoneSearch() {
      clearTimeout(this.phoneDebounce);
      this.phoneDebounce = setTimeout(() => {
        this.options = { ...this.options, page: 1 };
      }, 400);
    },
  },
  watch: {
    options: {
      handler() {
        this.loadMessages();
      },
      deep: true,
      immediate: true,
    },
    platform() {
      this.options = { ...this.options, page: 1 };
    },
    "filters.state"() {
      this.options = { ...this.options, page: 1 };
    },
    "filters.templateType"() {
      this.options = { ...this.options, page: 1 };
    },
    "filters.phone"() {
      this.schedulePhoneSearch();
    },
    "filters.companyId"() {
      if (
        this.platform &&
        this.filters.companyId &&
        this.platform.companyId !== this.filters.companyId
      ) {
        this.platform = null;
      }
      this.options = { ...this.options, page: 1 };
    },
    "selectedCompany.id"() {
      if (this.global) return;
      this.platform = null;
      this.options = { ...this.options, page: 1 };
    },
  },
  async mounted() {
    await this.loadCatalog();
  },
  beforeDestroy() {
    clearTimeout(this.phoneDebounce);
  },
};
</script>

<style scoped>
.gap-1 {
  gap: 4px;
}
.gap-2 {
  gap: 8px;
}
pre {
  max-height: 200px;
}
</style>
